/**
 * Один Блок на Раскладке (тикет T18b, спецификация § 4).
 *
 * Карточка знает про рамку, подпись и ручки правки; что внутри — приходит содержимым.
 * Разделено нарочно: Типов Блоков дюжина и будет больше, а рамка у всех одна, и
 * перетаскивание не должно переписываться при появлении тринадцатого Типа.
 *
 * Про клавиатуру. В режиме сборки карточка — это кнопка: на неё встаёт фокус, её читает
 * вслух диктор, Enter её «берёт». Без этого Раскладку собирал бы только тот, у кого есть
 * мышь, а критерий готовности (§ 15, п. 13) требует пройти раздел с клавиатуры.
 *
 * Про личный вид (правка 2026-08-30). Свёрнутость, краски и размер букв приходят сюда
 * готовыми и рисуются как есть: решение, можно ли так красить, принимается не здесь.
 * Цвет ставится прямо в оформление, поэтому карточка берёт только «#rrggbb» — тот же
 * образец, что проверяют сервер и шлюз, третьим рубежом.
 */
import { useState } from 'react';
import type { CSSProperties, KeyboardEvent, PointerEvent, ReactNode } from 'react';
import type { Position } from '@core/layout/grid.js';
import type { BlockTextScale, BlockView } from '@core/layout/editor.js';
import { DEFAULT_BLOCK_VIEW } from '@core/layout/editor.js';
import { BlockMenu } from './BlockMenu.js';
import { BlockSettings } from './BlockSettings.js';
import { useT } from '../state/settings.js';

/** Насколько крупнее обычного буквы в Блоке. Множитель поверх размера текста из Настроек. */
const SCALE_FACTOR: Readonly<Record<BlockTextScale, string>> = {
  small: '0.875em',
  normal: '1em',
  large: '1.2em',
  huge: '1.45em',
};

/** Цвет в оформление пускаем только правильного вида — последний рубеж перед разметкой. */
const safeColour = (colour: string | null): string | null =>
  colour && /^#[0-9a-f]{6}$/i.test(colour) ? colour : null;

export interface BlockCardProps {
  /** Номер Блока на карточке — по нему доска находит её, чтобы увести к ней экран. */
  readonly blockId: string;
  readonly title: string;
  readonly position: Position;
  /** Идёт сборка Раскладки: показываются ручки, работает перетаскивание. */
  readonly arranging: boolean;
  /** Этот Блок сейчас держат — рукой или с клавиатуры. */
  readonly grabbed: boolean;
  /** Этот Блок только что добавили: его обводят и подписывают, чтобы он нашёлся глазами. */
  readonly fresh?: boolean;
  readonly freshLabel?: string;
  readonly onGrabPointer?: (event: PointerEvent<HTMLElement>) => void;
  readonly onResizePointer?: (event: PointerEvent<HTMLElement>) => void;
  readonly onKeyDown?: (event: KeyboardEvent<HTMLElement>) => void;
  readonly onRemove?: () => void;
  /** Как Блок выглядит у смотрящего. Не передали — обычный. */
  readonly view?: BlockView;
  /** Правка личного вида: свернуть, скрыть, закрепить, покрасить. Нет — меню без этих пунктов. */
  readonly onView?: (patch: Partial<BlockView>) => void;
  /** «Перенести» из меню: взять Блок так же, как его берут с клавиатуры. */
  readonly onMove?: () => void;
  /** «Изменить размер» из меню: тот же захват, но стрелки меняют ширину и высоту. */
  readonly onResize?: () => void;
  /** Идёт разговор с сервером — пункты меню ждут. */
  readonly busy?: boolean;
  /** Узкий экран: Блоки идут столбиком, места на сетке у них нет. */
  readonly stacked?: boolean;
  readonly children: ReactNode;
}

export function BlockCard({
  blockId,
  title,
  position,
  arranging,
  grabbed,
  fresh = false,
  freshLabel,
  onGrabPointer,
  onResizePointer,
  onKeyDown,
  onRemove,
  view = DEFAULT_BLOCK_VIEW,
  onView,
  onMove,
  onResize,
  busy = false,
  stacked = false,
  children,
}: BlockCardProps): ReactNode {
  const t = useT();
  const [settingsOpen, setSettingsOpen] = useState(false);

  const background = safeColour(view.background);
  const foreground = safeColour(view.foreground);
  // Свёрнутый Блок занимает на сетке одну строку: оставь мы прежнюю высоту, на месте
  // свёрнутого зияла бы пустота, и сворачивание не освобождало бы ни клетки экрана.
  const rowsTaken = view.collapsed ? 1 : position.height;

  const placement: CSSProperties = stacked
    ? { minHeight: view.collapsed ? undefined : `${Math.max(position.height, 5) * 3.5}rem` }
    : {
        // Место на сетке считается от единицы, а хранится от нуля: так удобнее считать
        // расстановку и так же считает сервер.
        gridColumn: `${position.col + 1} / span ${position.width}`,
        gridRow: `${position.row + 1} / span ${rowsTaken}`,
      };

  const painted: CSSProperties = {
    ...placement,
    ...(background ? { backgroundColor: background } : {}),
    ...(foreground ? { color: foreground } : {}),
    ...(view.textScale ? { fontSize: SCALE_FACTOR[view.textScale] } : {}),
  };

  // Свой цвет букв перебивает общий только тогда, когда он задан: иначе класс темы должен
  // остаться, чтобы Блок менялся вместе с тёмным оформлением.
  const inkClass = foreground ? '' : 'text-ink';

  return (
    <section
      data-block={blockId}
      style={painted}
      aria-label={title}
      className={`relative flex min-h-0 flex-col overflow-hidden rounded-lg border ${
        background ? '' : 'bg-surface'
      } ${grabbed || fresh ? 'border-accent ring-2 ring-[var(--ktm-accent)]' : 'border-line'}`}
    >
      <header className="flex shrink-0 items-center justify-between gap-2 border-b border-line px-3 py-1.5">
        {arranging ? (
          <div
            // Ручка перетаскивания — она же то, на что встаёт фокус клавиатуры. Двух
            // разных мест для «взять Блок» быть не должно: человек, ходящий и мышью, и
            // клавиатурой, иначе каждый раз ищет, где сегодня работает.
            role="button"
            tabIndex={0}
            aria-pressed={grabbed}
            aria-label={`${title}. ${t('layout.position')}: ${position.col + 1}, ${position.row + 1}. ${t('layout.keyboardHint')}`}
            onPointerDown={onGrabPointer}
            onKeyDown={onKeyDown}
            className={`flex-1 cursor-grab select-none truncate text-sm font-medium ${inkClass}`}
          >
            {title}
          </div>
        ) : (
          <h3 className={`flex-1 truncate text-sm font-medium ${inkClass}`}>{title}</h3>
        )}

        {/* Закреплённый Блок помечен на виду, а не только в меню: человек, у которого он
            не берётся, должен видеть причину, не открывая ничего. */}
        {view.pinned ? (
          <span
            aria-label={t('block.pinned')}
            title={t('block.pinned')}
            className="shrink-0 text-xs text-ink-soft"
          >
            📌
          </span>
        ) : null}

        {/* Подпись у только что добавленного Блока. Обводки одной мало: она такая же, как
            у взятого Блока, и человек, плохо различающий цвет рамки, её не прочитает. */}
        {fresh && freshLabel ? (
          <span className="shrink-0 rounded bg-accent px-1.5 py-0.5 text-xs text-accent-ink">
            {freshLabel}
          </span>
        ) : null}

        {onView ? (
          <BlockMenu
            title={title}
            view={view}
            busy={busy}
            onView={onView}
            onSettings={() => setSettingsOpen((was) => !was)}
            {...(onRemove ? { onRemove } : {})}
            {...(onMove ? { onMove } : {})}
            {...(onResize ? { onResize } : {})}
          />
        ) : null}
      </header>

      {settingsOpen && onView ? (
        <BlockSettings
          view={view}
          busy={busy}
          onChange={onView}
          onClose={() => setSettingsOpen(false)}
        />
      ) : null}

      {/* Свёрнутый Блок — одна шапка. Содержимое не прячется стилем, а не рисуется вовсе:
          спрятанное стилем всё равно читает диктор, и «свёрнуто» звучало бы обманом. */}
      {view.collapsed ? null : (
        <div className="min-h-0 flex-1 overflow-auto p-3 text-sm">{children}</div>
      )}

      {arranging && !view.collapsed ? (
        <div
          // Уголок для растягивания — только для руки. С клавиатуры размер меняют
          // стрелками с Shift, взяв Блок за заголовок. Дай мы уголку фокус, человек
          // получил бы вторую остановку в обходе, делающую ровно то же самое, что
          // предыдущая, — а в такой лишней остановке всегда подозревают поломку.
          //
          // Свой признак `data-resize` — для живой приёмки. Раньше она искала уголок как
          // «первый aria-hidden в карточке», и появление трёх точек в шапке (они тоже
          // aria-hidden) увело мышь на кнопку меню: растягивание молча перестало
          // проверяться, а сценарий «Блок не схлопывается» светил зелёным, ничего не тронув.
          data-resize="true"
          aria-hidden="true"
          // Подпись при наведении — единственное, чем уголок объясняет себя мыши: диктору
          // он не показан нарочно (у клавиатуры свой путь), а глазами угадывать назначение
          // серого квадратика человек не обязан.
          title={t('layout.resizeHandle')}
          onPointerDown={onResizePointer}
          // Крупнее и заметнее прежнего (правка 2026-08-31). Уголок был в шестнадцать точек
          // и в цвет рамки: попасть в него мышью трудно, а увидеть — тем более, и растянуть
          // Блок оказывалось нечем. Теперь он двадцать восемь точек и залит цветом действия —
          // то есть читается как ручка, а не как край рамки.
          //
          // Значка внутри нарочно нет: стрелка вроде «⇲» есть не во всяком системном шрифте
          // и на части машин показалась бы пустым квадратом — а пустой квадрат в углу Блока
          // выглядит поломкой, ради избавления от которой правка и делалась.
          className="absolute bottom-0 end-0 size-7 cursor-se-resize rounded-tl bg-accent"
        />
      ) : null}
    </section>
  );
}
